/**
 * Plan card icon + accent color: the ONE resolver every surface uses
 * (/pricing, the SA plan card), so a plan looks the same everywhere.
 *
 * Jake, 2026-10-20: a plan's icon and accent are STORED on the plan
 * (`icon_key` / `accent_key`, keys from a server allow-list) and chosen by a
 * Super Admin. They never depend on the card's position: this resolver takes
 * the plan and nothing else. A stale, unknown or missing key resolves to the
 * one constant fallback (star + primary).
 *
 * The key lists live in `@/constants/planPresentation.json`, which
 * api/tests/test_plan_presentation_parity.py checks against the backend
 * allow-list (api/src/pricing/plan_presentation.py).
 */
import data from "@/constants/planPresentation.json";

export const PLAN_ICONS = Object.freeze(data.icons.map((i) => Object.freeze({ ...i })));
export const PLAN_ACCENTS = Object.freeze(data.accents.map((a) => Object.freeze({ ...a })));
export const DEFAULT_ICON_KEY = data.defaultIconKey;
export const DEFAULT_ACCENT_KEY = data.defaultAccentKey;

// Map lookups (not plain objects) so a key like "constructor" or
// "__proto__" can never resolve to an inherited property.
const ICON_BY_KEY = new Map(PLAN_ICONS.map((i) => [i.key, i]));
const ACCENT_BY_KEY = new Map(PLAN_ACCENTS.map((a) => [a.key, a]));

/**
 * @param {{icon_key?: string, accent_key?: string}|null|undefined} plan
 * @returns {{iconKey: string, icon: string, iconLabel: string,
 *            accentKey: string, accentHex: string, accentLabel: string}}
 */
export function resolvePlanPresentation(plan) {
    const icon =
        ICON_BY_KEY.get(plan && plan.icon_key) || ICON_BY_KEY.get(DEFAULT_ICON_KEY);
    const accent =
        ACCENT_BY_KEY.get(plan && plan.accent_key) ||
        ACCENT_BY_KEY.get(DEFAULT_ACCENT_KEY);
    return {
        iconKey: icon.key,
        // The key IS the Material Icons ligature name.
        icon: icon.key,
        iconLabel: icon.label,
        accentKey: accent.key,
        accentHex: accent.hex,
        accentLabel: accent.label,
    };
}
